/**
 * Utilities to format durations and distances consistently across Cartomancer,
 * with Italian labels expected by users (e.g. "1 Giorno 13 Ore e 23 Minuti", "14 Km").
 */

const UNIT_LABELS = {
  miles: "Miglia",
  km: "Km",
  leagues: "Leghe",
  hex: "Esagoni",
};

/**
 * Format a distance (numeric) using the map's unit into whole-number output.
 * Example: (14.7, "miles") -> "15 Miglia"
 */
export function formatDistance(val, unit) {
  if (!isFinite(val) || val == null) return "—";
  const rounded = Math.round(val);
  const label = UNIT_LABELS[unit] || unit || "";
  return `${rounded} ${label}`.trim();
}

/**
 * Format a duration expressed in decimal days into an Italian words format.
 * Examples:
 *   0.02083 (30 min) -> "30 Minuti"
 *   0.5 (12h)        -> "12 Ore"
 *   1.56 (1d 13h 26m)-> "1 Giorno 13 Ore e 26 Minuti"
 *   4.0              -> "4 Giorni"
 *   0                -> "0 Minuti"
 */
export function formatDurationDays(days) {
  if (!isFinite(days) || days == null) return "—";
  const totalMinutes = Math.max(0, Math.round(days * 24 * 60));
  const d = Math.floor(totalMinutes / (24 * 60));
  const rem = totalMinutes - d * 24 * 60;
  const h = Math.floor(rem / 60);
  const m = rem % 60;

  const parts = [];
  if (d > 0) parts.push(`${d} ${d === 1 ? "Giorno" : "Giorni"}`);
  if (h > 0) parts.push(`${h} ${h === 1 ? "Ora" : "Ore"}`);
  if (m > 0) parts.push(`${m} ${m === 1 ? "Minuto" : "Minuti"}`);

  if (parts.length === 0) return "0 Minuti";
  if (parts.length === 1) return parts[0];
  // Join with commas and final "e" (Italian style)
  const last = parts.pop();
  return `${parts.join(" ")} e ${last}`;
}

/**
 * Compact version for tight UIs (e.g. sidebar): "1g 13h 26m", "12h 30m", "45m".
 */
export function formatDurationCompact(days) {
  if (!isFinite(days) || days == null) return "—";
  const totalMinutes = Math.max(0, Math.round(days * 24 * 60));
  const d = Math.floor(totalMinutes / (24 * 60));
  const rem = totalMinutes - d * 24 * 60;
  const h = Math.floor(rem / 60);
  const m = rem % 60;
  const parts = [];
  if (d > 0) parts.push(`${d}g`);
  if (h > 0) parts.push(`${h}h`);
  if (m > 0) parts.push(`${m}m`);
  if (parts.length === 0) return "0m";
  return parts.join(" ");
}

/**
 * Clock HH:MM readout used by the in-game clock only (not for durations).
 */
export function formatClockHM(hourDecimal) {
  if (!isFinite(hourDecimal) || hourDecimal == null) return "00:00";
  let h = Math.floor(hourDecimal);
  let m = Math.round((hourDecimal - h) * 60);
  if (m === 60) { m = 0; h += 1; }
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(h % 24)}:${pad(m)}`;
}

/**
 * Add "days" to a base date {ig_day, ig_month, ig_year, ig_hour} using 30-day months.
 * Returns a formatted string "D° <Month> · Anno Y · HH:MM".
 */
export function formatDatePlus(base, addDays, months) {
  if (!base) return "";
  const totalHours = (base.ig_hour || 0) + addDays * 24;
  let dayInc = Math.floor(totalHours / 24);
  let hour = ((totalHours % 24) + 24) % 24;
  let day = (base.ig_day || 1) + dayInc;
  let month = base.ig_month || 1;
  let year = base.ig_year || 1;
  while (day > 30) {
    day -= 30;
    month += 1;
    if (month > (months?.length || 12)) {
      month = 1;
      year += 1;
    }
  }
  const monthName = (months || [])[month - 1] || `Mese ${month}`;
  return `${day}° ${monthName} · Anno ${year} · ${formatClockHM(hour)}`;
}
